// web-commands.ts — the PER-APP half of the web shape: maps solo_journal's Tauri data commands
// (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore. Plain CRUD
// is a few lines each. The derived commands (week_meta, week_signals) are computed on-device in
// Rust; on web they return a minimal shell for now so the UI/AI-reflection never crashes — a
// faithful web build ports that signal logic to JS (the known per-app wrinkle, see web docs).

import type { CommandHandler, WebStore } from "./web-runtime";
import { weekMetaWeb, weekSignalsWeb } from "./signals-web";
import type { JournalEntry as Entry } from "./store";
import { browserSearch, browserFetch } from "./web-search-browser";

const TABLE = "entries";

function uuid(): string {
  // crypto.randomUUID is available in every modern browser.
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    list_entries: ({ limit }) => {
      const rows = db.all(TABLE).sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
      return typeof limit === "number" ? rows.slice(0, limit) : rows;
    },
    // Mirrors db.rs::search_entries — every word must appear in the title or body.
    search_entries: ({ query, limit }) => {
      const words = String(query ?? "")
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 8);
      let rows = db.all(TABLE).sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
      if (words.length) {
        rows = rows.filter((r) => {
          const hay = `${String(r.title ?? "")} ${String(r.body ?? "")} ${String(r.tags ?? "")}`.toLowerCase();
          return words.every((w) => hay.includes(w));
        });
      }
      return typeof limit === "number" ? rows.slice(0, limit) : rows;
    },
    get_entry: ({ id }) => db.get(TABLE, String(id)),
    create_entry: ({ input }) => {
      const now = nowIso();
      const norm = (raw: unknown) => String(raw ?? "").split(",").map(t=>t.trim().toLowerCase())
        .filter(t=>t && t.length<=24).filter((t,i,a)=>a.indexOf(t)===i).slice(0,6).join(",");
      const row = {
        id: uuid(),
        created_at: now,
        updated_at: now,
        photo_ref: "",
        tags: "",
        place: "",
        // strip explicitly-undefined keys: they would clobber the defaults above (the Stash
        // file-a-box crash pattern, fleet sweep 2026-08-19)
        ...Object.fromEntries(Object.entries(input as Record<string, unknown>).filter(([, v]) => v !== undefined)),
      };
      row.tags = norm(row.tags);
      return db.insert(TABLE, row as { id: string } & Record<string, unknown>);
    },
    update_entry: ({ id, patch }) => {
      const norm = (raw: unknown) => String(raw ?? "").split(",").map(t=>t.trim().toLowerCase())
        .filter(t=>t && t.length<=24).filter((t,i,a)=>a.indexOf(t)===i).slice(0,6).join(",");
      const p = { ...(patch as Record<string, unknown>) };
      if ("tags" in p) p.tags = norm(p.tags);
      db.update(TABLE, String(id), { ...p, updated_at: nowIso() });
      return null;
    },
    delete_entry: ({ id }) => {
      db.remove(TABLE, String(id));
      return null;
    },
    // Derived — Rust in db.rs, faithfully ported in signals-web.ts. Both used to be empty shells,
    // so the reflection UI showed nothing on web and the BILLED reflection was handed a journal
    // with no entries in it.
    // ⚠️ METADATA ONLY: the port reads `body.length`, never the body text — same discipline as the
    // Rust query, which selects `length(body)` and never `body`.
    week_meta: () => weekMetaWeb(db.all(TABLE) as unknown as Entry[]),
    week_signals: ({ windowDays }) =>
      weekSignalsWeb(db.all(TABLE) as unknown as Entry[], Number(windowDays ?? 28)),
  };
}
